"use client";

import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, X } from "lucide-react";
import { contact } from "@/content/contact";
import {
  formatMobileNumber,
  getEmailError,
  getMobileNumberError,
} from "@/lib/formValidation";

type Status = "idle" | "loading" | "success" | "error";

const WEB3FORMS_ACCESS_KEY =
  process.env.NEXT_PUBLIC_WEB3FORMS_VITALZ_PRO_BROCHURE_ACCESS_KEY ??
  "c7bbf411-b16d-467e-95e1-b388c57cad4d";
export const countryCodes = [
  "+1", "+7", "+20", "+27", "+30", "+31", "+32", "+33", "+34", "+36",
  "+39", "+40", "+41", "+43", "+44", "+45", "+46", "+47", "+48", "+49",
  "+51", "+52", "+53", "+54", "+55", "+56", "+57", "+58", "+60", "+61",
  "+62", "+63", "+64", "+65", "+66", "+81", "+82", "+84", "+86", "+90",
  "+91", "+92", "+93", "+94", "+95", "+98", "+211", "+212", "+213", "+216",
  "+218", "+220", "+221", "+222", "+223", "+224", "+225", "+226", "+227", "+228",
  "+229", "+230", "+231", "+232", "+233", "+234", "+235", "+236", "+237", "+238",
  "+239", "+240", "+241", "+242", "+243", "+244", "+245", "+246", "+247", "+248",
  "+249", "+250", "+251", "+252", "+253", "+254", "+255", "+256", "+257", "+258",
  "+260", "+261", "+262", "+263", "+264", "+265", "+266", "+267", "+268", "+269",
  "+290", "+291", "+297", "+298", "+299", "+350", "+351", "+352", "+353", "+354",
  "+355", "+356", "+357", "+358", "+359", "+370", "+371", "+372", "+373", "+374",
  "+375", "+376", "+377", "+378", "+380", "+381", "+382", "+383", "+385", "+386",
  "+387", "+389", "+420", "+421", "+423", "+500", "+501", "+502", "+503", "+504",
  "+505", "+506", "+507", "+508", "+509", "+590", "+591", "+592", "+593", "+594",
  "+595", "+596", "+597", "+598", "+599", "+670", "+672", "+673", "+674", "+675",
  "+676", "+677", "+678", "+679", "+680", "+681", "+682", "+683", "+685", "+686",
  "+687", "+688", "+689", "+690", "+691", "+692", "+850", "+852", "+853", "+855",
  "+856", "+880", "+886", "+960", "+961", "+962", "+963", "+964", "+965", "+966",
  "+967", "+968", "+970", "+971", "+972", "+973", "+974", "+975", "+976", "+977",
  "+992", "+993", "+994", "+995", "+996", "+998",
] as const;

type BrochureDownloadButtonProps = {
  brochureHref: string;
  downloadFilename: string;
  variant?: "light" | "dark";
};

export function BrochureDownloadButton({
  brochureHref,
  downloadFilename,
  variant = "dark",
}: BrochureDownloadButtonProps) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [phoneNumber, setPhoneNumber] = useState("");
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    nameInputRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), iframe, [href]',
        ),
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [open]);

  const startDownload = () => {
    const downloadLink = document.createElement("a");
    downloadLink.href = brochureHref;
    downloadLink.download = downloadFilename;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");

    const form = event.currentTarget;
    const formData = new FormData(form);
    const fullName = String(formData.get("fullName") || "");
    const email = String(formData.get("email") || "");
    const botcheck = String(formData.get("botcheck") || "");

    const nextEmailError = getEmailError(email);
    if (nextEmailError) {
      setEmailError(nextEmailError);
      return;
    }

    const mobileNumberError = getMobileNumberError(countryCode, phoneNumber);
    if (mobileNumberError) {
      setPhoneError(mobileNumberError);
      return;
    }

    setStatus("loading");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: "New VitalZ-Pro brochure download from HealthVoitho.com",
          from_name: "HealthVoitho Website",
          name: fullName,
          email,
          contact_number: formatMobileNumber(countryCode, phoneNumber),
          product: "VitalZ-Pro® technical brochure",
          message: "Please send me the VitalZ-Pro® technical brochure.",
          botcheck,
        }),
      });
      const data = (await response.json()) as {
        success?: boolean;
        message?: string;
      };

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Something went wrong.");
      }

      startDownload();
      form.reset();
      setCountryCode("+91");
      setPhoneNumber("");
      setEmailError("");
      setPhoneError("");
      setStatus("success");
    } catch (submissionError) {
      setStatus("error");
      setError(
        submissionError instanceof Error
          ? submissionError.message
          : "Something went wrong.",
      );
    }
  };

  const triggerClassName =
    variant === "light"
      ? "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-[#102e3a] shadow-[0_14px_35px_rgba(3,20,29,0.22)] outline-none focus-visible:ring-2 focus-visible:ring-[#c3cf95] focus-visible:ring-offset-2 focus-visible:ring-offset-[#102e3a] sm:text-base"
      : "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-[#102e3a] px-6 py-3.5 text-sm font-semibold text-white shadow-[0_14px_35px_rgba(16,46,58,0.18)] outline-none focus-visible:ring-2 focus-visible:ring-[#468397] focus-visible:ring-offset-2 sm:text-base";

  return (
    <>
      <button
        type="button"
        className={triggerClassName}
        onClick={() => {
          setStatus("idle");
          setError("");
          setEmailError("");
          setPhoneError("");
          setCountryCode("+91");
          setPhoneNumber("");
          setOpen(true);
        }}
      >
        Download technical brochure
        <Download className="h-4 w-4" aria-hidden />
      </button>

      {open
        ? createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-[#071f2d]/78 px-3 py-4 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setOpen(false);
          }}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="relative max-h-[calc(100dvh-1.5rem)] w-full max-w-sm overflow-y-auto bg-white p-4 shadow-[0_28px_90px_rgba(3,20,29,0.34)] sm:p-5"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full text-[#42565b] outline-none focus-visible:ring-2 focus-visible:ring-[#468397]"
              aria-label="Close brochure form"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>

            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#2f7f80]">
              VitalZ-Pro®
            </p>
            <h2 id={titleId} className="mt-1.5 pr-9 font-display text-2xl leading-tight text-[#1d315f]">
              Download the technical brochure
            </h2>

            {status === "success" ? (
              <div className="mt-4 border-t border-[#d7e1df] pt-4" aria-live="polite">
                <p className="text-sm leading-6 text-[#42565b]">{contact.success}</p>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="mt-4 inline-flex w-full items-center justify-center rounded-full bg-[#102e3a] px-6 py-2.5 text-sm font-semibold text-white outline-none focus-visible:ring-2 focus-visible:ring-[#468397] focus-visible:ring-offset-2"
                >
                  Close
                </button>
              </div>
            ) : (
              <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
                <input
                  type="checkbox"
                  name="botcheck"
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                />
                <label className="block">
                  <span className="text-sm font-semibold text-[#263f45]">
                    Full name
                  </span>
                  <input
                    ref={nameInputRef}
                    type="text"
                    name="fullName"
                    autoComplete="name"
                    placeholder="Enter your full name"
                    required
                    className="mt-1 h-9 w-full border border-[#bdcecb] bg-white px-3 text-sm text-[#1a2426] outline-none focus:border-[#468397] focus:ring-2 focus:ring-[#468397]/20"
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-semibold text-[#263f45]">
                    Email address
                  </span>
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    placeholder="Enter your email address"
                    required
                    aria-invalid={Boolean(emailError)}
                    aria-describedby={
                      emailError ? `${titleId}-email-error` : undefined
                    }
                    onBlur={(event) =>
                      setEmailError(getEmailError(event.target.value))
                    }
                    onChange={() => {
                      if (emailError) setEmailError("");
                    }}
                    className="mt-1 h-9 w-full border border-[#bdcecb] bg-white px-3 text-sm text-[#1a2426] outline-none focus:border-[#468397] focus:ring-2 focus:ring-[#468397]/20"
                  />
                  {emailError ? (
                    <span
                      id={`${titleId}-email-error`}
                      className="mt-1.5 block text-xs text-red-700"
                      role="alert"
                    >
                      {emailError}
                    </span>
                  ) : null}
                </label>

                <fieldset>
                  <legend className="text-sm font-semibold text-[#263f45]">
                    Contact number
                  </legend>
                  <div className="mt-1 grid grid-cols-[7.25rem_1fr] gap-2">
                    <label className="sr-only" htmlFor={`${titleId}-country-code`}>
                      Country code
                    </label>
                    <select
                      id={`${titleId}-country-code`}
                      name="countryCode"
                      value={countryCode}
                      required
                      onChange={(event) => {
                        const nextCountryCode = event.target.value;
                        setCountryCode(nextCountryCode);
                        setPhoneError(
                          getMobileNumberError(nextCountryCode, phoneNumber),
                        );
                      }}
                      className="h-9 min-w-0 border border-[#bdcecb] bg-white px-2 text-xs text-[#1a2426] outline-none focus:border-[#468397] focus:ring-2 focus:ring-[#468397]/20"
                    >
                      {countryCodes.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                    </select>
                    <label className="sr-only" htmlFor={`${titleId}-phone-number`}>
                      Mobile number
                    </label>
                    <input
                      id={`${titleId}-phone-number`}
                      type="tel"
                      name="phoneNumber"
                      autoComplete="tel-national"
                      inputMode="numeric"
                      required
                      pattern="[0-9]+"
                      title="Enter numbers only"
                      placeholder="Enter your mobile number"
                      value={phoneNumber}
                      aria-invalid={Boolean(phoneError)}
                      aria-describedby={
                        phoneError ? `${titleId}-phone-error` : undefined
                      }
                      onBlur={() =>
                        setPhoneError(
                          getMobileNumberError(countryCode, phoneNumber),
                        )
                      }
                      onChange={(event) => {
                        setPhoneNumber(
                          event.target.value.replace(/\D/g, "").slice(0, 15),
                        );
                        if (phoneError) setPhoneError("");
                      }}
                      className="h-9 min-w-0 border border-[#bdcecb] bg-white px-3 text-sm text-[#1a2426] outline-none focus:border-[#468397] focus:ring-2 focus:ring-[#468397]/20"
                    />
                  </div>
                  {phoneError ? (
                    <p
                      id={`${titleId}-phone-error`}
                      className="mt-1.5 text-xs text-red-700"
                      role="alert"
                    >
                      {phoneError}
                    </p>
                  ) : null}
                </fieldset>

                <label className="flex items-start gap-2.5 border-t border-[#d7e1df] pt-3 text-xs leading-[1.15rem] text-[#42565b]">
                  <input
                    type="checkbox"
                    name="contactConsent"
                    defaultChecked
                    required
                    className="mt-1 h-4 w-4 shrink-0 accent-[#2f7f80]"
                  />
                  <span>
                    By submitting this form, you confirm that HealthVoitho may
                    contact you using the details provided.
                  </span>
                </label>

                {error ? (
                  <p className="text-xs text-red-700" role="alert">
                    {error}
                  </p>
                ) : null}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#102e3a] px-6 py-2.5 text-sm font-semibold text-white outline-none focus-visible:ring-2 focus-visible:ring-[#468397] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60"
                >
                  {status === "loading" ? "Sending…" : "Submit and download"}
                  <Download className="h-4 w-4" aria-hidden />
                </button>
              </form>
            )}
          </div>
        </div>,
            document.body,
          )
        : null}
    </>
  );
}
